// react
import { useCallback, useEffect, useState } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// types
import { Feedback, Project } from '../../../@types/index';

// router
import { useSearchParams } from 'react-router-dom';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Alert, AlertTitle, Grid, Paper, Typography } from '@mui/material';

// tables
import { TableProjects } from '../../tables/cards/TableProjects/TableProjects';

// forms
import { FormSearch } from '../FormSearch/FormSearch';

// icons
import {
	SearchOutlined as IconSearch,
	SearchOffOutlined as IconSearchOff,
} from '@mui/icons-material';

export const FormSearchProjects = () => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const { t } = useTranslation();

	// state
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [hasSearched, setHasSearched] = useState(false);
	const [results, setResults] = useState<Project[]>([]);
	const [searchParams] = useSearchParams();

	// params
	const paramIsMock = searchParams.get('isMock');
	const paramQ = searchParams.get('q');

	// vars
	const idForm = 'form-search-projects';

	let urlSearch = `${process.env.REACT_APP_API_URL}/wbg?q=${paramQ}&resources=project,loans`;

	if (paramIsMock) {
		urlSearch += `&resource=mock`;
	}

	const getProjectsOnboarded = useCallback(async () => {
		const fetchProjectsOnboarded = await fetch(
			`${process.env.REACT_APP_API_URL}/projects?fields=idWbg`,
			{
				headers: {
					Authorization: `Bearer ${accessToken}`,
					User: String(idToken),
				},
			}
		);

		const resProjectsOnboarded = await fetchProjectsOnboarded.json();

		if (resProjectsOnboarded.data) {
			return resProjectsOnboarded.data.map((project: Project) => project.idWbg.toLowerCase());
		}

		return [];
	}, [accessToken, idToken]);

	// lazy search fetch
	const { fetchRequest: fetchResults, isLoading } = useFetch({
		isLazy: true,
		url: urlSearch,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: async (res) => {
			if (res.data) {
				setResults(res.data);
			}
			setHasSearched(true);
		},
	});

	// vars
	const CardEmptyProps = {
		empty: {
			content: { children: t('empty.projects.content') },
			IconCircleProps: { icon: <IconSearch /> },
			title: { children: t('empty.projects.title') },
		},
	};

	if (hasSearched && !isLoading) {
		CardEmptyProps.empty = {
			content: { children: t('empty.default.content') },
			IconCircleProps: { icon: <IconSearchOff /> },
			title: { children: t('empty.default.title') },
		};
	}

	useEffect(() => {
		const search = async () => {
			setFeedback(null);

			if (paramQ) {
				const projectsOnboarded = await getProjectsOnboarded();
				const isOnboarded = projectsOnboarded.includes(paramQ.toLowerCase());

				if (isOnboarded) {
					const feedbackNew = {
						alert: {
							children: t('error.projectAlreadyOnboarded', {
								idWbg: paramQ,
							}),
						},
					};

					setFeedback(feedbackNew);
				} else {
					fetchResults();
				}
			}
		};

		search();
	}, [fetchResults, getProjectsOnboarded, paramQ, t]);

	return (
		<Grid container spacing={2}>
			<Grid item xs={12}>
				<Typography variant="h2">{t('search')}</Typography>
			</Grid>
			<Grid item xs={12}>
				<Paper
					sx={(theme) => {
						return {
							padding: {
								xs: theme.spacing(2),
								md: theme.spacing(3),
							},
						};
					}}>
					<Grid container spacing={2}>
						<Grid item xs={12} md={true}>
							<FormSearch
								id={idForm}
								InputProps={{
									helperText: t('formSearchProjects.q.helper'),
									id: 'q',
									name: 'q',
									placeholder: t('searchById'),
								}}
								LoadingButtonProps={{
									fullWidth: true,
									loading: isLoading,
								}}
							/>
						</Grid>
						{feedback && (
							<Grid item xs={12}>
								<Alert severity="error" {...feedback.alert}>
									{feedback.title && <AlertTitle {...feedback.title} />}
									{feedback.alert.children}
								</Alert>
							</Grid>
						)}
					</Grid>
				</Paper>
			</Grid>
			<Grid item xs={12}>
				<Typography variant="h2">{t('results')}</Typography>
			</Grid>

			<Grid item xs={12}>
				<TableProjects
					CardEmptyProps={CardEmptyProps}
					hasSearch={false}
					isLoading={isLoading}
					isMock={Boolean(paramIsMock)}
					isOnboarded={false}
					projects={results}
				/>
			</Grid>
		</Grid>
	);
};
